import React, { useRef, useState } from "react";
import { Avatar, Button, useConfirm, useToast } from "../../../design-system";
import { useAvatarBlobUrl } from "../../../helpers/useAvatarBlobUrl";

/**
 * Widget reusable para subir/borrar la foto de un employee o user.
 *
 * Props:
 *  - `name`: nombre completo (para iniciales si no hay foto)
 *  - `hasAvatar`: bool — viene del backend (`has_avatar`)
 *  - `avatarUrl`: ruta API del avatar (e.g. `/me/avatar` o
 *    `/employees/{id}/avatar`). Solo se fetchea si `hasAvatar`.
 *  - `canEdit`: bool — gating de los botones (UI). El backend
 *    re-autoriza siempre.
 *  - `onSet(file)`: handler para subir. Debe devolver una promise.
 *  - `onRemove()`: handler para borrar. Debe devolver una promise.
 *  - `size`: px del avatar (default 96)
 */
export function AvatarUploader({
  name,
  hasAvatar,
  avatarUrl,
  canEdit = false,
  onSet,
  onRemove,
  size = 96,
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Bump cuando hay cambios para refetchear el blob.
  const [version, setVersion] = useState(0);

  const { blobUrl } = useAvatarBlobUrl(hasAvatar ? avatarUrl : null, {
    enabled: !!hasAvatar,
    version,
  });

  const trigger = () => {
    if (busy || !canEdit) return;
    fileRef.current?.click();
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    setBusy(true);
    try {
      await onSet?.(f);
      setVersion((v) => v + 1);
      toast.success("Foto actualizada");
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "No se pudo subir la foto.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onClickRemove = async () => {
    const ok = await confirm({
      title: "Quitar foto",
      description: "¿Seguro que quieres quitar la foto? Se mostrarán las iniciales en su lugar.",
      confirmLabel: "Quitar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await onRemove?.();
      setVersion((v) => v + 1);
      toast.success("Foto eliminada");
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "No se pudo quitar la foto.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ position: "relative" }}>
        <Avatar name={name || "?"} size={size} src={blobUrl || undefined} />
        {canEdit && (
          <button
            type="button"
            onClick={trigger}
            disabled={busy}
            aria-label="Cambiar foto"
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 32,
              height: 32,
              borderRadius: 999,
              border: "2px solid var(--kr-bg-surface)",
              background: "var(--kr-action)",
              color: "#fff",
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            ✎
          </button>
        )}
      </div>
      {canEdit && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              icon="Upload"
              onClick={trigger}
              disabled={busy}
            >
              {hasAvatar ? "Cambiar foto" : "Subir foto"}
            </Button>
            {hasAvatar && (
              <Button
                variant="ghost"
                size="sm"
                icon="Trash2"
                onClick={onClickRemove}
                disabled={busy}
              >
                Quitar
              </Button>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
            JPG, PNG o WebP · máx 5 MB · se recorta a cuadrado.
          </div>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        onChange={onPick}
        style={{ display: "none" }}
      />
    </div>
  );
}

export default AvatarUploader;
